import PropTypes from 'prop-types';
import { createContext, useCallback, useMemo } from 'react';

// project imports
import baseConfig, { MenuOrientation } from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// use a new key so old saved values won't override your defaults
const STORAGE_KEY = 'mantis-config-v2';

// --- SERIALIZABLE defaults only (no functions here) ---
const DEFAULTS = {
  // start from whatever your base config provides (if it exists)
  ...baseConfig,

  // Your requested defaults:
  mode: 'light',                  // theme mode
  presetColor: 'default',          // color scheme
  themeDirection: 'ltr',          // menu direction

  menuOrientation: MenuOrientation.VERTICAL,
  miniDrawer: true,               // mini drawer

  container: false,               // false => Fluid, true => Container/boxed
  fontFamily: "'Public Sans', sans-serif"
};

// This is the shape consumers get from useConfig()
const ConfigContext = createContext({
  ...DEFAULTS,
  onChangeContainer: () => {},
  onChangeLocalization: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeDirection: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMenuOrientation: () => {},
  onChangeFontFamily: () => {}
});

function ConfigProvider({ children }) {
  // Persist only the serializable state
  const [state, setState] = useLocalStorage(STORAGE_KEY, DEFAULTS);

  // -------- handlers update serializable state only --------
  const onChangeContainer = useCallback(
    (container) => setState((prev) => ({ ...prev, container })),
    [setState]
  );

  const onChangeLocalization = useCallback(
    (lang) => setState((prev) => ({ ...prev, i18n: lang })),
    [setState]
  );

  const onChangeMode = useCallback(
    (mode) => setState((prev) => ({ ...prev, mode })),
    [setState]
  );

  const onChangePresetColor = useCallback(
    (theme) => setState((prev) => ({ ...prev, presetColor: theme })),
    [setState]
  );

  const onChangeDirection = useCallback(
    (direction) => setState((prev) => ({ ...prev, themeDirection: direction })),
    [setState]
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer) =>
      setState((prev) => ({
        ...prev,
        menuOrientation: MenuOrientation.VERTICAL, // mini uses vertical
        miniDrawer
      })),
    [setState]
  );

  const onChangeMenuOrientation = useCallback(
    (layout) =>
      setState((prev) => ({
        ...prev,
        menuOrientation: layout,
        ...(layout === MenuOrientation.VERTICAL ? { miniDrawer: false } : {})
      })),
    [setState]
  );

  const onChangeFontFamily = useCallback(
    (fontFamily) => setState((prev) => ({ ...prev, fontFamily })),
    [setState]
  );

  // Final value exposed to the app (serializable state + functions)
  const value = useMemo(
    () => ({
      ...state,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangePresetColor,
      onChangeDirection,
      onChangeMiniDrawer,
      onChangeMenuOrientation,
      onChangeFontFamily
    }),
    [
      state,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangePresetColor,
      onChangeDirection,
      onChangeMiniDrawer,
      onChangeMenuOrientation,
      onChangeFontFamily
    ]
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export { ConfigProvider, ConfigContext };
ConfigProvider.propTypes = { children: PropTypes.node };
